import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { CircleUserRound, Shield } from 'lucide-react';
import { getPickableKeys, getRegistryEntry } from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import type { PlaceholderKind } from 'modules/manager/domain/placeholder/types';
import type { TemplateType } from 'modules/manager/domain/template-type';
import React, { useMemo, useRef } from 'react';
import UDModal from 'ud-ui/components/modal';
import { editorAccent } from '../editor-accent';
import { theme } from 'styles/theme/theme';

interface PlaceholderKeyPickerModalProps {
  kind: PlaceholderKind;
  /** Unknown (still loading) — every key of the kind is offered, as in the inspector. */
  templateType?: TemplateType;
  onPick: (key: PlaceholderKey) => void;
  onClose: () => void;
}

// Image keys have no text sample — a glyph tells a logo from a photo at a glance.
const IMAGE_ICONS: Partial<Record<PlaceholderKey, JSX.Element>> = {
  [PlaceholderKey.TEAM_LOGO]: <Shield strokeWidth={1.5} />,
  [PlaceholderKey.TOURNAMENT_LOGO]: <Shield strokeWidth={1.5} />,
  [PlaceholderKey.PLAYER_PHOTO]: <CircleUserRound strokeWidth={1.5} />,
};

/** Mount only while shown: the parent renders it conditionally, so a reopen starts clean. */
const PlaceholderKeyPickerModal: React.FC<PlaceholderKeyPickerModalProps> = ({ kind, templateType, onPick, onClose }) => {
  const { t } = useLingui();
  const keys = useMemo(() => getPickableKeys(kind, templateType), [kind, templateType]);
  // A double click must not drop two placeholders: the first pick wins, the
  // modal unmounts right after.
  const picked = useRef(false);

  const pick = (key: PlaceholderKey) => {
    if (picked.current) return;
    picked.current = true;
    onPick(key);
    onClose();
  };

  return (
    <UDModal show onClose={onClose} size="lg">
      <UDModal.Header title={kind === 'placeholder-image' ? t`Выберите изображение` : t`Выберите данные`} />
      <UDModal.Body>
        <Grid>
          {keys.map((key) => {
            const entry = getRegistryEntry(key);
            if (!entry) return null;
            const icon = IMAGE_ICONS[key];
            return (
              <Card key={key} type="button" data-key={key} onClick={() => pick(key)}>
                <Name>{t(entry.label)}</Name>
                {entry.sample ? <Sample>{t(entry.sample)}</Sample> : icon && <Icon data-testid="placeholder-key-icon">{icon}</Icon>}
              </Card>
            );
          })}
        </Grid>
      </UDModal.Body>
    </UDModal>
  );
};

export default React.memo(PlaceholderKeyPickerModal);

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

// Same frame as the layout preset card next door — one look for the editor's pickers.
const Card = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid ${theme.colors.DarkBlue100};
  border-radius: 6px;
  background: ${theme.colors.SurfaceWhite};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${theme.colors.DarkBlue300};
  }

  &:focus-visible {
    outline: none;
    border-color: ${editorAccent.solid};
    box-shadow: 0 0 0 2px ${editorAccent.ring};
  }
`;

const Name = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.DarkBlue};
`;

const Sample = styled.span`
  font-size: 12px;
  color: ${theme.colors.DarkBlue600};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Icon = styled.span`
  display: flex;
  color: ${theme.colors.DarkBlue600};

  svg {
    width: 20px;
    height: 20px;
  }
`;
